import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  PackageOpen,
  Pencil,
  Trash2,
  Box,
  CalendarDays,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  EmptyState,
  SkeletonLoader,
  useToast,
} from "@/components/system";
import { formatError } from "@/lib/format-error";

/**
 * BW12.5 Issue 12 — Pantry item detail.
 *
 * Header (image + brand + name) → stock summary → batches table → nutrition
 * → recent activity → action toolbar (open / adjust / delete / waste).
 */

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

type Batch = {
  id: string;
  quantity: number;
  unit: string;
  unit_price: number | null;
  unit_price_currency: string | null;
  unopened_until: string | null;
  opened_at: string | null;
  effective_expiry: string | null;
  source_receipt_line_id: string | null;
};

type DetailRow = {
  id: string;
  user_id: string;
  food_id: string;
  location: string;
  qty: number;
  unit: string;
  opened: boolean;
  purchased: string;
  expires: string | null;
  staple: boolean;
  restock_priority: string | null;
  notes: string;
  nutrition_refreshed_at: string | null;
  food: {
    id: string;
    name: string;
    brand: string | null;
    kcal: number | null;
    kcal_per_100g: number | null;
    protein_g_per_100g: number | null;
    carb_g_per_100g: number | null;
    fat_g_per_100g: number | null;
    fiber_g_per_100g: number | null;
    image_url: string | null;
    basis: string | null;
    nutrition_source: string | null;
    nutrition_confidence: string | null;
  } | null;
  batches: Batch[];
};

export function PantryDetail() {
  const { id } = useParams<{ id: string }>();
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [adjusting, setAdjusting] = useState(false);
  const [adjustQty, setAdjustQty] = useState("");

  const q = useQuery({
    queryKey: ["pantry_detail", id],
    enabled: Boolean(id) && Boolean(userId),
    staleTime: 15_000,
    queryFn: async (): Promise<DetailRow | null> => {
      if (!id || !userId) return null;
      // PostgREST embed needs the actual pantry_item_batch column names —
      // schema landed in BW12 with `quantity, unopened_until,
      // source_receipt_line_id`, not the BW11 placeholder names. Querying
      // the wrong columns turned every detail page into "Couldn't load
      // item." (BW17 Issue 1).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supa = supabase as unknown as { from: (t: string) => any };
      const { data, error } = await supa
        .from("pantry_item")
        .select(
          `id, user_id, food_id, location, qty, unit, opened, purchased,
           expires, staple, restock_priority, notes, nutrition_refreshed_at,
           food:food!inner(id, name, brand, kcal, kcal_per_100g,
             protein_g_per_100g, carb_g_per_100g, fat_g_per_100g,
             fiber_g_per_100g, image_url, basis,
             nutrition_source, nutrition_confidence),
           batches:pantry_item_batch(id, quantity, unit, unit_price,
             unit_price_currency, unopened_until, opened_at,
             effective_expiry, source_receipt_line_id)`,
        )
        .eq("id", id)
        .eq("user_id", userId)
        .maybeSingle();
      if (error) {
        // Supabase PostgrestError isn't an Error instance; preserve its
        // descriptive `message` so the UI surfaces the real cause instead
        // of the generic fallback. (BW17.7: surfaces the real error
        // instead of the generic fallback so future schema mismatches are
        // visible immediately.)
        console.error("[PantryDetail] supabase error:", error);
        throw new Error(error.message ?? "Couldn't load item.");
      }
      return data as unknown as DetailRow | null;
    },
  });

  const markOpened = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No id");
      const { error } = await supabase
        .from("pantry_item")
        .update({ opened: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pantry_detail", id] });
      qc.invalidateQueries({ queryKey: ["pantry", userId] });
      toast.show("Marked as opened.");
    },
    onError: (e) => toast.show(`Couldn't mark opened: ${formatError(e)}`),
  });

  // BW20.3d: re-run the lookup chain for the backing food row, update the
  // cached macros, and bump pantry_item.nutrition_refreshed_at + version.
  const refreshNutrition = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No id");
      const { data: sessionRes } = await supabase.auth.getSession();
      const accessToken = sessionRes.session?.access_token;
      if (!accessToken) throw new Error("Sign in to refresh nutrition.");
      const resp = await fetch(`${WORKER_URL}/api/pantry/${id}/refresh-nutrition`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
          "content-type": "application/json",
        },
      });
      const json = (await resp.json()) as
        | {
            status: "ok" | "no_match" | "error";
            source_after?: string | null;
            kcal_after?: number | null;
            error?: string;
          }
        | { error: string };
      if (!resp.ok || ("error" in json && !("status" in json))) {
        throw new Error(("error" in json && json.error) || `HTTP ${resp.status}`);
      }
      return json as { status: "ok" | "no_match" | "error"; source_after?: string | null; kcal_after?: number | null; error?: string };
    },
    onSuccess: (out) => {
      qc.invalidateQueries({ queryKey: ["pantry_detail", id] });
      qc.invalidateQueries({ queryKey: ["pantry", userId] });
      if (out.status === "ok") {
        toast.show(
          `Updated. Source: ${out.source_after ?? "?"}, ${Math.round(Number(out.kcal_after ?? 0))} kcal/100g.`,
        );
      } else if (out.status === "no_match") {
        toast.show("No match found. Try editing the food name or snap a photo.");
      } else {
        toast.show(`Refresh failed: ${out.error ?? "unknown"}`);
      }
    },
    onError: (e) => toast.show(`Refresh failed: ${formatError(e)}`),
  });

  const adjust = useMutation({
    mutationFn: async (newQty: number) => {
      if (!id) throw new Error("No id");
      const { error } = await supabase
        .from("pantry_item")
        .update({ qty: newQty })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pantry_detail", id] });
      qc.invalidateQueries({ queryKey: ["pantry", userId] });
      toast.show("Quantity updated.");
      setAdjusting(false);
    },
    onError: (e) => toast.show(`Couldn't update quantity: ${formatError(e)}`),
  });

  const del = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No id");
      const { error } = await supabase.from("pantry_item").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pantry", userId] });
      toast.show("Removed from pantry.");
      navigate("/pantry");
    },
    onError: (e) => toast.show(`Couldn't delete: ${formatError(e)}`),
  });

  const waste = useMutation({
    mutationFn: async (reason: string) => {
      if (!q.data) throw new Error("Not loaded");
      const row = q.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supa = supabase as never as { from: (t: string) => any };
      const { error: wasteErr } = await supa
        .from("pantry_waste")
        .insert({
          user_id: userId!,
          pantry_item_id: row.id,
          food_id: row.food_id,
          wasted_quantity: row.qty,
          unit: row.unit,
          reason,
        });
      // Soft-tolerate missing pantry_waste table (older deploys); deletion still
      // happens so the user isn't stuck.
      if (wasteErr && !/relation .* does not exist/i.test(wasteErr.message)) {
        throw wasteErr;
      }
      const { error: delErr } = await supabase
        .from("pantry_item")
        .delete()
        .eq("id", row.id);
      if (delErr) throw delErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pantry", userId] });
      toast.show("Logged as waste.");
      navigate("/pantry");
    },
    onError: (e) => toast.show(`Couldn't log waste: ${formatError(e)}`),
  });

  const batches = useMemo(() => {
    const xs = q.data?.batches ?? [];
    // FEFO sort: nearest effective_expiry first; fall back to unopened_until.
    return [...xs].sort((a, b) => {
      const ae = a.effective_expiry ?? a.unopened_until ?? "9999-12-31";
      const be = b.effective_expiry ?? b.unopened_until ?? "9999-12-31";
      return ae.localeCompare(be);
    });
  }, [q.data]);

  if (q.isLoading) return <SkeletonLoader variant="card" count={3} />;
  if (q.isError) {
    return (
      <p role="alert" style={{ color: "var(--brick)" }}>
        {q.error instanceof Error ? q.error.message : "Couldn't load item."}
      </p>
    );
  }
  if (!q.data) {
    return (
      <EmptyState
        headline="Item not found."
        sub="It may have been removed already."
      />
    );
  }
  const row = q.data;
  const food = row.food;

  return (
    <article aria-labelledby="pantry-detail-heading">
      <Link
        to="/pantry"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          color: "var(--fg-3)",
          fontSize: "var(--type-body-s)",
          textDecoration: "none",
          marginBottom: "var(--space-4)",
        }}
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> All pantry items
      </Link>

      <header
        style={{
          display: "grid",
          gridTemplateColumns: food?.image_url ? "96px 1fr max-content" : "1fr max-content",
          gap: "var(--space-4)",
          alignItems: "center",
          marginBottom: "var(--space-6)",
        }}
      >
        {food?.image_url ? (
          <img
            src={food.image_url}
            alt=""
            width={96}
            height={96}
            style={{
              borderRadius: "var(--radius-card)",
              objectFit: "cover",
            }}
          />
        ) : null}
        <div>
          {food?.brand && (
            <p
              style={{
                fontSize: "var(--type-micro)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
                margin: 0,
              }}
            >
              {food.brand}
            </p>
          )}
          <h1
            id="pantry-detail-heading"
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-s)",
              margin: "var(--space-2) 0 0",
            }}
          >
            {food?.name ?? "Pantry item"}
          </h1>
        </div>
        <button
          type="button"
          aria-label="Delete pantry item"
          title="Delete"
          onClick={() => {
            if (confirm("Delete this pantry item? This cannot be undone.")) del.mutate();
          }}
          disabled={del.isPending}
          style={{
            width: 36,
            height: 36,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            color: "var(--brick)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            cursor: del.isPending ? "not-allowed" : "pointer",
          }}
        >
          <Trash2 size={16} strokeWidth={1.5} />
        </button>
      </header>

      <section
        aria-label="Stock"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "var(--space-3)",
          marginBottom: "var(--space-6)",
        }}
      >
        <Stat
          label="On hand"
          value={`${row.qty} ${row.unit}`}
          subtle={row.opened ? "opened" : "sealed"}
        />
        <Stat
          label="Location"
          value={row.location.replace(/_/g, " ")}
          subtle={row.staple ? "staple" : null}
        />
        <Stat
          label="Restock"
          value={row.restock_priority ?? "—"}
          subtle={null}
        />
        <Stat
          label="Earliest expiry"
          value={
            row.expires
              ? new Date(row.expires + "T00:00:00").toLocaleDateString()
              : "—"
          }
          subtle={null}
        />
      </section>

      <section
        aria-label="Batches"
        style={{
          marginBottom: "var(--space-6)",
        }}
      >
        <h2 style={sectionHeader()}>Batches</h2>
        {batches.length === 0 ? (
          <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
            One batch (the original purchase). Receipt scans will split this out
            into individual lots when used.
          </p>
        ) : (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {batches.map((b) => (
              <li
                key={b.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr max-content max-content",
                  gap: "var(--space-3)",
                  padding: "var(--space-3) 0",
                  borderBottom: "1px solid var(--hair)",
                  fontSize: "var(--type-body-s)",
                  color: "var(--fg)",
                }}
              >
                <span>
                  <CalendarDays
                    size={12}
                    strokeWidth={1.5}
                    style={{ marginRight: 6, color: "var(--fg-3)" }}
                  />
                  Unopened until{" "}
                  {b.unopened_until
                    ? new Date(b.unopened_until + "T00:00:00").toLocaleDateString()
                    : "—"}
                  {b.opened_at && (
                    <span style={{ color: "var(--accent)", marginLeft: 8 }}>
                      · opened{" "}
                      {new Date(b.opened_at).toLocaleDateString()}
                    </span>
                  )}
                </span>
                <span className="mono" style={{ color: "var(--fg-2)" }}>
                  {b.quantity} {b.unit}
                </span>
                <span
                  className="mono"
                  style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}
                >
                  {b.effective_expiry ?? b.unopened_until ?? "—"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(food?.kcal_per_100g ||
        food?.kcal ||
        food?.protein_g_per_100g ||
        food?.carb_g_per_100g ||
        food?.fat_g_per_100g) && (
        <section
          aria-label="Nutrition"
          style={{
            padding: "var(--space-4)",
            background: "var(--bg-2)",
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            marginBottom: "var(--space-6)",
          }}
        >
          <header
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: "var(--space-2)",
              marginBottom: "var(--space-3)",
              flexWrap: "wrap",
            }}
          >
            <h2 style={{ ...sectionHeader(), marginBottom: 0 }}>
              Nutrition, per {food?.basis === "per_100ml" ? "100ml" : "100g"}
            </h2>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
              <NutritionProvenance
                source={food?.nutrition_source ?? null}
                confidence={food?.nutrition_confidence ?? null}
              />
              <button
                type="button"
                onClick={() => refreshNutrition.mutate()}
                disabled={refreshNutrition.isPending}
                title={
                  row.nutrition_refreshed_at
                    ? `Last refreshed ${daysAgo(row.nutrition_refreshed_at)}`
                    : "Never refreshed"
                }
                style={{
                  height: 28,
                  padding: "0 var(--space-3)",
                  background: "transparent",
                  color: "var(--fg-2)",
                  border: "1px solid var(--hair-2)",
                  borderRadius: "var(--radius-chip)",
                  fontSize: "var(--type-meta)",
                  cursor: refreshNutrition.isPending ? "progress" : "pointer",
                }}
              >
                {refreshNutrition.isPending ? "Refreshing…" : "Refresh nutrition"}
              </button>
            </div>
          </header>
          <div
            className="mono"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
              gap: "var(--space-3)",
              fontSize: "var(--type-body-s)",
              color: "var(--fg)",
            }}
          >
            <NutLabel label="kcal" value={food?.kcal_per_100g ?? food?.kcal ?? null} />
            <NutLabel label="protein" value={food?.protein_g_per_100g ?? null} unit="g" />
            <NutLabel label="carbs" value={food?.carb_g_per_100g ?? null} unit="g" />
            <NutLabel label="fat" value={food?.fat_g_per_100g ?? null} unit="g" />
            <NutLabel label="fiber" value={food?.fiber_g_per_100g ?? null} unit="g" />
          </div>
        </section>
      )}

      {adjusting && (
        <section
          aria-label="Adjust quantity"
          style={{
            padding: "var(--space-4)",
            background: "var(--bg-2)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            marginBottom: "var(--space-4)",
            display: "flex",
            gap: "var(--space-3)",
            alignItems: "center",
          }}
        >
          <input
            type="number"
            inputMode="decimal"
            value={adjustQty}
            onChange={(e) => setAdjustQty(e.target.value)}
            placeholder={String(row.qty)}
            style={{
              height: 38,
              padding: "0 var(--space-3)",
              background: "var(--bg-3)",
              color: "var(--fg)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              flex: 1,
              minWidth: 0,
            }}
          />
          <span style={{ color: "var(--fg-3)" }}>{row.unit}</span>
          <button
            type="button"
            onClick={() => {
              const n = Number(adjustQty);
              if (Number.isFinite(n) && n >= 0) adjust.mutate(n);
            }}
            disabled={adjust.isPending || !adjustQty}
            style={primaryStyle()}
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setAdjusting(false)}
            disabled={adjust.isPending}
            style={secondaryStyle()}
          >
            Cancel
          </button>
        </section>
      )}

      <section
        aria-label="Actions"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--space-2)",
          marginBottom: "var(--space-8)",
        }}
      >
        <button
          type="button"
          onClick={() => markOpened.mutate()}
          disabled={row.opened || markOpened.isPending}
          style={secondaryStyle()}
        >
          <PackageOpen size={14} strokeWidth={1.5} />
          {row.opened ? "Opened" : "Mark opened"}
        </button>
        <button
          type="button"
          onClick={() => {
            setAdjustQty(String(row.qty));
            setAdjusting(true);
          }}
          style={secondaryStyle()}
        >
          <Pencil size={14} strokeWidth={1.5} />
          Adjust quantity
        </button>
        <button
          type="button"
          onClick={() => {
            const reason = prompt("What happened? (e.g. expired, spoiled)") ?? "";
            if (reason.trim()) waste.mutate(reason.trim());
          }}
          disabled={waste.isPending}
          style={{ ...secondaryStyle(), color: "var(--amber)" }}
        >
          <Sparkles size={14} strokeWidth={1.5} />
          Mark wasted
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm("Remove this item from your pantry?")) del.mutate();
          }}
          disabled={del.isPending}
          style={{ ...secondaryStyle(), color: "var(--brick)" }}
        >
          <Trash2 size={14} strokeWidth={1.5} />
          Delete
        </button>
      </section>
    </article>
  );
}

function Stat({
  label,
  value,
  subtle,
}: {
  label: string;
  value: string;
  subtle: string | null;
}) {
  return (
    <div
      style={{
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "var(--space-2) 0 0",
          fontSize: "var(--type-body-l)",
          color: "var(--fg)",
          fontWeight: 500,
          textTransform: "capitalize",
        }}
      >
        {value}
      </p>
      {subtle && (
        <p
          style={{
            margin: "var(--space-1) 0 0",
            fontSize: "var(--type-meta)",
            color: "var(--fg-3)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {subtle}
        </p>
      )}
    </div>
  );
}

function NutritionProvenance({
  source,
  confidence,
}: {
  source: string | null;
  confidence: string | null;
}) {
  if (!source && !confidence) {
    return (
      <span
        style={{
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Unverified
      </span>
    );
  }
  const sourceLabel: Record<string, string> = {
    off: "Open Food Facts",
    usda: "USDA",
    manual: "Entered manually",
    photo_extract: "Label scan",
    unknown: "Unverified",
  };
  const dot =
    confidence === "high"
      ? "var(--olive, #6a8d4a)"
      : confidence === "medium"
        ? "var(--amber, #c8a64a)"
        : confidence === "low"
          ? "var(--brick, #a45050)"
          : "var(--fg-3)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: "var(--type-meta)",
        color: "var(--fg-3)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 9999,
          background: dot,
          flexShrink: 0,
        }}
      />
      {source ? sourceLabel[source] ?? source : "Unverified"}
    </span>
  );
}

function NutLabel({
  label,
  value,
  unit,
}: {
  label: string;
  value: number | null;
  unit?: string;
}) {
  return (
    <div>
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "var(--space-1) 0 0",
          color: "var(--fg)",
          fontSize: "var(--type-body)",
        }}
      >
        {value == null ? "—" : Math.round(Number(value))}
        {value != null && unit ? unit : ""}
      </p>
    </div>
  );
}

function sectionHeader(): React.CSSProperties {
  return {
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: "var(--type-body-l)",
    color: "var(--fg)",
    margin: 0,
    marginBottom: "var(--space-3)",
  };
}

function secondaryStyle(): React.CSSProperties {
  return {
    height: 38,
    padding: "0 var(--space-4)",
    background: "transparent",
    color: "var(--fg)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    cursor: "pointer",
    fontSize: "var(--type-body-s)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
}

function primaryStyle(): React.CSSProperties {
  return {
    height: 38,
    padding: "0 var(--space-5)",
    background: "var(--accent)",
    color: "#0a0b0d",
    border: "1px solid transparent",
    borderRadius: "var(--radius-card)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "var(--type-body-s)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
}

function daysAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const days = Math.max(0, Math.floor(ms / 86400000));
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

// Suppress unused-import warning when the icon isn't yet used in some tree-shaken builds.
void Box;
